// Confirmation modal shown when the user edits a primary-key column in the
// data grid. Fetches inbound foreign-key references for the row's table and
// surfaces what will cascade / fail before staging the UPDATE.
//
// Props:
//   open                  — boolean
//   database, table       — for the title + the API call
//   connectionId          — for the API call
//   oldPK / newPK         — {[col]: value} maps (PK columns only)
//   onConfirm()           — user clicked Confirm
//   onCancel()            — user clicked Cancel / Esc / outside-click
//
// Loading: while the FK fetch is in flight, Confirm is disabled and a "Checking
// references…" line is shown. Error: if the fetch fails we still let the user
// proceed (the engine is the final arbiter) but warn that the reference list
// could not be loaded.
import { createResource, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { loadInboundFKs } from '../lib/tableschema.js';

const fmtVal = (v) => {
  if (v === null || v === undefined) return 'NULL';
  if (typeof v === 'string') return `'${v}'`;
  return String(v);
};

const cascadeRules = (refs) => {
  const out = { cascade: 0, restrict: 0, setNull: 0, noAction: 0, setDefault: 0 };
  for (const r of refs) {
    const u = (r.onUpdate || '').toUpperCase();
    if (u === 'CASCADE') out.cascade++;
    else if (u === 'SET NULL') out.setNull++;
    else if (u === 'SET DEFAULT') out.setDefault++;
    else if (u === 'NO ACTION') out.noAction++;
    else out.restrict++; // RESTRICT (the implicit default)
  }
  return out;
};

export default function PKEditConfirmModal(props) {
  // Fetch only when the modal opens — load the inbound FK list. createResource's
  // signal func returning a falsy value skips the fetch entirely.
  const [refs] = createResource(
    () => (props.open ? [props.connectionId, props.database, props.table] : null),
    async ([id, db, t]) => {
      try {
        const r = await loadInboundFKs(id, db, t);
        return r?.references || [];
      } catch (e) {
        return { error: e.message || 'failed to load references' };
      }
    }
  );

  const errored = () => refs() && typeof refs() === 'object' && 'error' in refs();
  const list = () => (Array.isArray(refs()) ? refs() : []);
  const rules = createMemo(() => cascadeRules(list()));
  // The danger banner fires whenever at least one referencing FK uses anything
  // other than CASCADE on UPDATE — those will fail (RESTRICT/NO ACTION) or
  // silently null out rows (SET NULL / SET DEFAULT).
  const hasBlockingRule = () =>
    rules().restrict > 0 || rules().noAction > 0 || rules().setNull > 0 || rules().setDefault > 0;

  // Old → new PK display, one row per changed column (the parent passes only
  // the changed PK columns, but we render whatever's in newPK for clarity).
  const diffRows = createMemo(() => {
    const out = [];
    const oldMap = props.oldPK || {};
    const newMap = props.newPK || {};
    for (const k of Object.keys(newMap)) {
      out.push({ col: k, oldVal: oldMap[k], newVal: newMap[k] });
    }
    return out;
  });

  return (
    <Modal
      open={!!props.open}
      title="Confirm primary-key change"
      onClose={props.onCancel}
      closeOnBackdrop={false}
      wide={true}
    >
      <div class="pk-confirm">
        <p class="muted" style="font-size:12px;margin:0 0 8px;">
          You're about to change the primary key on{' '}
          <span class="mono">{props.database}.{props.table}</span>.
        </p>

        <div class="pk-confirm-diff">
          <For each={diffRows()}>
            {(d) => (
              <div class="pk-confirm-diff-row mono">
                <span class="pk-confirm-col">{d.col}</span>
                <span class="pk-confirm-old">{fmtVal(d.oldVal)}</span>
                <span class="pk-confirm-arrow">→</span>
                <span class="pk-confirm-new">{fmtVal(d.newVal)}</span>
              </div>
            )}
          </For>
        </div>

        <Show when={refs.loading}>
          <p class="muted" style="font-size:12px;margin:10px 0 0;">Checking references…</p>
        </Show>

        <Show when={!refs.loading && errored()}>
          <div class="pk-confirm-warn" role="alert">
            ⚠ Couldn't load foreign-key references ({refs()?.error}). The engine
            will still enforce constraints — you can proceed and rely on the
            error if it fails.
          </div>
        </Show>

        <Show when={!refs.loading && !errored()}>
          <Show
            when={list().length > 0}
            fallback={
              <p class="muted" style="font-size:12px;margin:10px 0 0;">
                No other tables reference this table's primary key. The change
                won't cascade.
              </p>
            }
          >
            <div class="pk-confirm-refs">
              <div class="pk-confirm-refs-head">
                {list().length} table{list().length === 1 ? '' : 's'} reference{list().length === 1 ? 's' : ''} this PK:
              </div>
              <table class="pk-confirm-refs-table mono">
                <thead>
                  <tr>
                    <th>Referencing table</th>
                    <th>Columns</th>
                    <th>ON UPDATE</th>
                    <th>ON DELETE</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={list()}>
                    {(r) => (
                      <tr classList={{ 'pk-confirm-row-blocker': (r.onUpdate || '').toUpperCase() !== 'CASCADE' }}>
                        <td>
                          {r.referencingSchema && r.referencingSchema !== props.database
                            ? `${r.referencingSchema}.${r.referencingTable}`
                            : r.referencingTable}
                        </td>
                        <td>{(r.referencingColumns || []).join(', ')}</td>
                        <td>{r.onUpdate || 'RESTRICT'}</td>
                        <td>{r.onDelete || 'RESTRICT'}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <Show when={hasBlockingRule()}>
                <div class="pk-confirm-warn" role="alert">
                  ⚠ Some referencing constraints aren't <span class="mono">ON UPDATE CASCADE</span>.
                  The UPDATE will fail (RESTRICT / NO ACTION) or null out
                  referencing rows (SET NULL / SET DEFAULT). The engine error,
                  if any, will appear in the SQL log.
                </div>
              </Show>
            </div>
          </Show>
        </Show>

        <div class="restore-actions">
          <button class="btn btn-ghost" onClick={props.onCancel}>Cancel</button>
          <button class="btn btn-primary" onClick={props.onConfirm} disabled={refs.loading}>
            Confirm — stage the change
          </button>
        </div>
      </div>
    </Modal>
  );
}
